// src/components/staff/HoursAccountCard.js

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import HoursAccountService from '../../services/HoursAccountService';
import { formatSignedHours, formatHours } from '../../utils/DurationCalculator';
import '../../styles/hours-account.css';

/**
 * HoursAccountCard
 *
 * Displays the signed YTD balance hero, live current-month figure, monthly
 * breakdown table, and opening balance / adjustment summary for a staff
 * member's hours account.
 *
 * Self-contained — manages its own Firestore load via HoursAccountService.ensureAccount.
 * No context dependency.
 *
 * Props:
 *   staffId          {string}   Required
 *   companyId        {string}   Required
 *   onManageClick    {function} Called with (year) when the manage button is clicked
 *   onAccountLoaded  {function} Called with (account) after successful load or create
 *   refreshTrigger   {number}   Increment to force a reload after modal save
 */
function HoursAccountCard({
  staffId,
  companyId,
  onManageClick,
  onAccountLoaded,
  refreshTrigger,
}) {
  const { t, i18n } = useTranslation('staff');

  const [hoursAccount, setHoursAccount]     = useState(null);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear]     = useState(
    new Date().getFullYear().toString()
  );
  const [retryCount, setRetryCount]         = useState(0);

  // ---------------------------------------------------------------------------
  // Populate year selector once on mount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 1; i <= currentYear + 2; i++) {
      years.push(i.toString());
    }
    setAvailableYears(years);
  }, []);

  // ---------------------------------------------------------------------------
  // Load account whenever staffId, companyId, selectedYear, refreshTrigger,
  // or retryCount changes
  // ---------------------------------------------------------------------------

  const loadAccount = useCallback(async () => {
    if (!staffId || !companyId || !selectedYear) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const account = await HoursAccountService.ensureAccount(
        companyId,
        staffId,
        selectedYear
      );
      setHoursAccount(account);
      if (onAccountLoaded) {
        onAccountLoaded(account);
      }
    } catch (err) {
      console.error('Error loading hours account:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [staffId, companyId, selectedYear, refreshTrigger, retryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  const handleManageClick = () => {
    if (onManageClick) {
      onManageClick(selectedYear);
    }
  };

  const handleRetry = () => {
    setError(null);
    setRetryCount((prev) => prev + 1);
  };

  // ---------------------------------------------------------------------------
  // Display helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns a CSS class name based on the signed balance value.
   * Threshold of 0.5 h (30 min) treated as neutral to avoid showing red/green
   * for negligible rounding differences.
   */
  const getBalanceColor = (balance) => {
    if (Math.abs(balance) < 0.5) return 'balance-neutral';
    if (balance > 0) return 'balance-positive';
    return 'balance-negative';
  };

  /**
   * Returns only the closed month keys, sorted chronologically ("01"…"12").
   */
  const getSortedMonths = (monthlyBalances) => {
    return Object.keys(monthlyBalances || {})
      .filter((k) => monthlyBalances[k].closed)
      .sort();
  };

  /**
   * Returns the current month's balance entry if it exists and is not yet
   * closed — i.e. the provisional figure written nightly by the Cloud
   * Function. Only relevant when the selected year is the current year.
   */
  const getLiveCurrentMonth = (monthlyBalances, year) => {
    const now = new Date();
    const currentYear = now.getFullYear().toString();
    if (year !== currentYear) return null;

    const key = String(now.getMonth() + 1).padStart(2, '0');
    const m = (monthlyBalances || {})[key];
    if (!m || m.closed) return null;

    return { key, ...m };
  };

  /**
   * Returns the localised full month name for a two-digit month key.
   * Uses the active i18n language so DE and EN display correctly.
   */
  const getMonthName = (monthKey) => {
    const date = new Date(2000, parseInt(monthKey, 10) - 1, 1);
    return date.toLocaleDateString(
      i18n.language === 'de' ? 'de-DE' : 'en-US',
      { month: 'long' }
    );
  };

  /**
   * Formats a Date to a short German-locale datetime string.
   */
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('de-DE', {
      day:    '2-digit',
      month:  '2-digit',
      year:   'numeric',
      hour:   '2-digit',
      minute: '2-digit',
    });
  };

  // ---------------------------------------------------------------------------
  // Shared header (used in loading and error states)
  // ---------------------------------------------------------------------------

  const renderHeader = (withControls = false) => (
    <div className="card-header">
      <div className="header-left">
        <h3>{t('hoursAccount.title')}</h3>
      </div>
      {withControls && (
        <div className="header-right">
          <select
            value={selectedYear}
            onChange={handleYearChange}
            className="year-selector"
            aria-label={t('hoursAccount.title')}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <button
            className="btn-secondary manage-btn"
            onClick={handleManageClick}
          >
            {t('hoursAccount.manageAccount')}
          </button>
        </div>
      )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render: loading
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="hours-account-card">
        {renderHeader(false)}
        <div className="loading-container">
          <div className="spinner" />
          <p>{t('page.loading')}</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: error
  // ---------------------------------------------------------------------------

  if (error) {
    return (
      <div className="hours-account-card">
        {renderHeader(true)}
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="btn-secondary" onClick={handleRetry}>
            {t('timeManagement.refresh')}
          </button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Safety checks after a successful load
  // ---------------------------------------------------------------------------

  if (!hoursAccount) {
    return (
      <div className="hours-account-card">
        {renderHeader(true)}
        <div className="error-container">
          <p className="error-message">
            {t('hoursAccount.errorSavingAccount')}
          </p>
          <button className="btn-secondary" onClick={handleRetry}>
            {t('timeManagement.refresh')}
          </button>
        </div>
      </div>
    );
  }

  if (typeof hoursAccount.ytdBalance !== 'number') {
    console.error('Invalid hours account data:', hoursAccount);
    return (
      <div className="hours-account-card">
        {renderHeader(true)}
        <div className="error-container">
          <p className="error-message">
            {t('hoursAccount.errorSavingAccount')}
          </p>
          <button className="btn-secondary" onClick={handleRetry}>
            {t('timeManagement.refresh')}
          </button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: main
  // ---------------------------------------------------------------------------

  const sortedMonths = getSortedMonths(hoursAccount.monthlyBalances);
  const liveMonth = getLiveCurrentMonth(hoursAccount.monthlyBalances, selectedYear);

  return (
    <div className="hours-account-card">

      {/* Header */}
      <div className="card-header">
        <div className="header-left">
          <h3>{t('hoursAccount.title')}</h3>
        </div>
        <div className="header-right">
          <select
            value={selectedYear}
            onChange={handleYearChange}
            className="year-selector"
            aria-label={t('hoursAccount.title')}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <button
            className="btn-secondary manage-btn"
            onClick={handleManageClick}
          >
            {t('hoursAccount.manageAccount')}
          </button>
        </div>
      </div>

      {/* YTD Balance Hero */}
      <div className="ytd-balance-section">
        <div
          className={`ytd-balance-value ${getBalanceColor(hoursAccount.ytdBalance)}`}
        >
          {formatSignedHours(hoursAccount.ytdBalance)}
        </div>
        <div className="ytd-balance-label">
          {t('hoursAccount.thisYear')} {selectedYear}
        </div>
        <div className="ytd-balance-meta">
          <span>
            {t('hoursAccount.openingBalance')}:{' '}
            {formatSignedHours(hoursAccount.openingBalance)}
          </span>
          {hoursAccount.adjustments?.length > 0 && (
            <span>
              {t('hoursAccount.adjustments')} ({hoursAccount.adjustments.length}):{' '}
              {formatSignedHours(hoursAccount.getTotalAdjustmentHours())}
            </span>
          )}
        </div>
      </div>

      {/* Monthly Breakdown */}
      <div className="breakdown-section">
        <h4 className="breakdown-title">
          {t('hoursAccount.monthlyBreakdown')}
        </h4>

        {/* Live / provisional current month — written nightly by the Cloud
            Function with closed:false. Only rendered when the selected year
            is the current year and the current month hasn't been closed yet. */}
        {liveMonth && (
          <div className="live-month-row">
            <span className="live-month-label">
              {getMonthName(liveMonth.key)}{' '}
              <span className="live-month-badge">
                {t('hoursAccount.liveProvisional')}
              </span>
            </span>
            <span className="live-month-figures">
              <span className="live-month-figure">
                {t('hoursAccount.worked')}: {formatHours(liveMonth.worked)}
              </span>
              <span className="live-month-figure">
                {t('hoursAccount.target')}: {formatHours(liveMonth.target)}
              </span>
              <span
                className={`live-month-figure ${getBalanceColor(liveMonth.balance)}`}
              >
                {formatSignedHours(liveMonth.balance)}
              </span>
            </span>
          </div>
        )}

        {sortedMonths.length === 0 ? (
          <p className="no-closed-months">
            {t('hoursAccount.noClosedMonths')}
          </p>
        ) : (
          <table className="monthly-breakdown-table">
            <thead>
              <tr>
                <th>{t('hoursAccount.month')}</th>
                <th>{t('hoursAccount.worked')}</th>
                <th>{t('hoursAccount.target')}</th>
                <th>{t('hoursAccount.balance')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedMonths.map((monthKey) => {
                const m = hoursAccount.monthlyBalances[monthKey];
                return (
                  <tr key={monthKey}>
                    <td>{getMonthName(monthKey)}</td>
                    <td>{formatHours(m.worked)}</td>
                    <td>{formatHours(m.target)}</td>
                    <td className={getBalanceColor(m.balance)}>
                      {formatSignedHours(m.balance)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <p className="live-month-note">
          {t('hoursAccount.liveMonthNote')}
        </p>
      </div>

      {/* Footer */}
      <div className="card-footer">
        <span className="last-updated">
          {t('hoursAccount.lastCalculated')}:{' '}
          {hoursAccount.lastCalculatedAt
            ? formatDate(hoursAccount.lastCalculatedAt)
            : t('hoursAccount.neverCalculated')}
        </span>
      </div>

    </div>
  );
}

export default HoursAccountCard;